import React, { useContext, useMemo, useRef } from "react";
import { useEffect, useState } from "react";
import Chips from "../../Utils/BasicComponents/Chips";
import Tabs from "../../Utils/BasicComponents/Tabs";
import JobCard from "../../Utils/Card/JobCard";
import axios from "axios";
import { urlLinks } from "../../UrlLinks";
import SingleDropdown from "../../Utils/Dropdown/SingleDropdown";
import { MyContext } from "../../context/Context";
import ScaleLoader from "react-spinners/ScaleLoader";
import moment from "moment";
import information from "../../assets/information.svg";
import noData from "../../assets/noData.svg";
import { debounce } from "../../Utils/utilFunctions";

const TaskPage = () => {
  const {
    onboardToken,
    setOnboardToken,
    selectedOption,
    setSelectedOption,
    loading,
    setLoading,
    setIsNavbarVisible,
  } = useContext(MyContext);
  const abortControllerRef = useRef({
    processInstance: [],
  });
  const [projectList, setProjectList] = useState([]);
  const [dataFetched, setDataFetched] = useState(false);
  const tabOptions = [
    {
      id: "Open",
      text: "Open/Rework",
    },
    {
      id: "Submitted",
      text: "Submitted",
    },
    {
      id: "QcDone",
      text: "QC Done",
    },
  ];
  const [selectedTab, setSelectedTab] = useState(tabOptions[0]);
  const dynamicFilterOptions =
    selectedTab.id === "Open"
      ? [
          {
            id: "All",
            text: "All",
          },
          {
            id: "Rework",
            text: "Rework",
          },
          {
            id: "Overdue",
            text: "Overdue",
          },
          {
            id: "Draft",
            text: "Draft",
          },
        ]
      : selectedTab.id === "Submitted"
      ? [
          {
            id: "All",
            text: "All",
          },
          {
            id: "QcPending",
            text: "QC Pending",
          },
        ]
      : selectedTab.id === "QcDone"
      ? [
          {
            id: "Approved",
            text: "Approved ",
          },
          {
            id: "Rejected",
            text: "Rejected ",
          },
        ]
      : [];
  const dynamicFilterOptionsObj =
    selectedTab.id === "Open"
      ? {
          All: {
            id: "All",
            text: "All",
            instanceData: null,
          },
          Rework: {
            id: "Rework",
            text: "Rework",
            instanceData: null,
          },
          Overdue: {
            id: "Overdue",
            text: "Overdue",
            instanceData: null,
          },
          Draft: {
            id: "Draft",
            text: "Draft",
            instanceData: null,
          },
        }
      : selectedTab.id === "Submitted"
      ? {
          All: {
            id: "All",
            text: "All",
            instanceData: null,
          },
          QcPending: {
            id: "QcPending",
            text: "QC Pending",
            instanceData: null,
          },
        }
      : selectedTab.id === "QcDone"
      ? {
          Approved: {
            id: "Approved",
            text: "Approved ",
            instanceData: null,
          },
          Rejected: {
            id: "Rejected",
            text: "Rejected ",
            instanceData: null,
          },
        }
      : {};
  const [filterOptions, setFilterOptions] = useState(dynamicFilterOptions);
  const [selectedFilterOption, setSelectedFilterOption] = useState(
    dynamicFilterOptions[0].id
  );
  const [processData, setProcessData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [taskStart, setTaskStart] = useState(0);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [hasMoreTasks, setHasMoreTasks] = useState(false);
  const [processInstanceData, setProcessInstanceData] = useState(
    dynamicFilterOptionsObj
  );
  const [selectedUfo, setSelectedUfo] = useState(null);

  // Handle scrolling
  const handleScroll = () => {
    if (
      window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 100 &&
      !loadingTasks &&
      hasMoreTasks
    ) {
      // When user scrolls near the bottom of the page, load more tasks
      if (taskStart >= selectedUfo.total) return setHasMoreTasks(false);
      setTaskStart((prevStart) => prevStart + 10);
    }
  };

  const handleInitialData = async () => {
    setTaskStart(0);
    setTableData([]);
    setHasMoreTasks(false);
  };

  const handleTab = async (option, i) => {
    await handleInitialData();
    setSelectedTab(option);
  };

  const updateProcessInstanceData = async (keyId, responseObj) => {
    return new Promise((resolve) => {
      let newInstanceData = null;

      setProcessInstanceData((prev) => {
        newInstanceData = {
          ...(prev || {}),
          [keyId]: {
            ...(dynamicFilterOptionsObj[keyId] || {}),
            instanceData: responseObj,
          },
        };
        return newInstanceData; // This updates the state
      });

      // Use a short delay to allow React to update state asynchronously
      setTimeout(() => {
        resolve(newInstanceData);
      }, 10);
    });
  };

  const getScrolledProcessInstances = async (taskListRequest) => {
    try {
      const response = await axios.post(
        `${urlLinks.baseUrlTask}${urlLinks.taskExecution.processInstances}`,
        taskListRequest,
        {
          headers: {
            Authorization: `${onboardToken.authCode}`,
          },
        }
      );
      let responseObjData = response?.data?.data?.data;

      const newInstanceData = {
        ...processInstanceData,
        [selectedFilterOption]: {
          ...processInstanceData[selectedFilterOption],
          instanceData: {
            ...processInstanceData[selectedFilterOption].instanceData,
            data: [
              ...processInstanceData[selectedFilterOption].instanceData?.data,
              ...responseObjData,
            ],
            start: taskStart,
          },
        },
      };
      setProcessInstanceData(newInstanceData);
      return newInstanceData;
    } catch (error) {
      return [];
    }
  };

  const getProcessInstances = async (taskListRequest, option) => {
    // Create a new AbortController for this request
    const controller = new AbortController();
    const signal = controller.signal;

    // Add this controller to the ref array to track it
    abortControllerRef.current.processInstance.push(controller);

    try {
      let keyId = option.id;

      const response = await axios.post(
        `${urlLinks.baseUrlTask}${urlLinks.taskExecution.processInstances}`,
        taskListRequest,
        {
          headers: {
            Authorization: `${onboardToken.authCode}`,
          },
          signal, // signal is part of the config object
        }
      );
      let responseObj = response.data.data;

      const data = await updateProcessInstanceData(keyId, responseObj);
      return [data, false];
    } catch (error) {
      if (error.name === "CanceledError") {
        console.log("Request canceled");
      } else {
        console.log("Error occurred", error.message);
      }
      return [[], true];
    }
  };

  const getTaskListRequestData = (
    stageValue,
    finished,
    rawQueryArray,
    multiQueryArray,
    option
  ) => {
    switch (selectedTab.id) {
      case "Open":
        finished = false;
        if (option?.id === "All" || option === "All") {
          stageValue = "ASND";
          rawQueryArray = [
            {
              name: "wf_assigned_to",
              value: `%${onboardToken?.emailDecrypted}%`,
              type: "string",
              operation: "likeIgnoreCase",
              variableOperation: "LIKE_IGNORE_CASE",
            },
          ];
          multiQueryArray = [
            {
              name: "entityStage",
              type: "string",
              multipeValue: ["ASND", "REWK"],
              value: null,
              operation: "likeIgnoreCase",
              variableOperation: "LIKE_IGNORE_CASE",
            },
          ];
        }
        if (option?.id === "Rework" || option === "Rework") {
          stageValue = "REWK";
          rawQueryArray = [
            {
              name: "wf_assigned_to",
              value: `%${onboardToken.emailDecrypted}%`,
              type: "string",
              operation: "likeIgnoreCase",
              variableOperation: "LIKE_IGNORE_CASE",
            },
          ];
          multiQueryArray = [
            {
              name: "entityStage",
              type: "string",
              multipeValue: ["REWK"],
              value: null,
              operation: "likeIgnoreCase",
              variableOperation: "LIKE_IGNORE_CASE",
            },
          ];
        }
        if (option?.id === "Overdue" || option === "Overdue") {
          stageValue = "ASND";
          rawQueryArray = [
            {
              name: "wf_assigned_to",
              value: `%${onboardToken?.emailDecrypted}%`,
              type: "string",
              operation: "likeIgnoreCase",
              variableOperation: "LIKE_IGNORE_CASE",
            },
            {
              name: "is_tat_expired",
              value: true,
              type: "boolean",
              operation: "equals",
              variableOperation: "EQUALS",
            },
          ];
          multiQueryArray = [
            {
              name: "entityStage",
              type: "string",
              multipeValue: ["ASND", "REWK"],
              value: null,
              operation: "likeIgnoreCase",
              variableOperation: "LIKE_IGNORE_CASE",
            },
          ];
        }
        if (option?.id === "Draft" || option === "Draft") {
          stageValue = "ASND";
          rawQueryArray = [
            {
              name: "wf_assigned_to",
              value: `%${onboardToken?.emailDecrypted}%`,
              type: "string",
              operation: "likeIgnoreCase",
              variableOperation: "LIKE_IGNORE_CASE",
            },
            {
              name: "is_draft",
              value: "%1%",
              type: "string",
              operation: "likeIgnoreCase",
              variableOperation: "LIKE_IGNORE_CASE",
            },
          ];
          multiQueryArray = [
            {
              name: "entityStage",
              type: "string",
              multipeValue: ["ASND", "REWK"],
              value: null,
              operation: "likeIgnoreCase",
              variableOperation: "LIKE_IGNORE_CASE",
            },
          ];
        }
        break;
      case "Submitted":
        finished = false;
        stageValue = null;
        rawQueryArray = [
          {
            name: "wf_assigned_to",
            value: `%${onboardToken.emailDecrypted}%`,
            type: "string",
            operation: "likeIgnoreCase",
            variableOperation: "LIKE_IGNORE_CASE",
          },
        ];
        multiQueryArray = [
          {
            name: "entityStage",
            type: "string",
            multipeValue: ["SBMT", "QCAL", "QCD"],
            value: null,
            operation: "likeIgnoreCase",
            variableOperation: "LIKE_IGNORE_CASE",
          },
        ];
        break;
      case "QcDone":
        const today = moment().format("YYYY-MM-DD");
        const dateBefore30Days = moment()
          .subtract(30, "days")
          .format("YYYY-MM-DD");

        finished = true;
        stageValue = null;
        rawQueryArray = [
          {
            name: "wf_assigned_to",
            value: `%${onboardToken.emailDecrypted}%`,
            type: "string",
            operation: "likeIgnoreCase",
            variableOperation: "LIKE_IGNORE_CASE",
          },
          {
            name: "qc_submitted_on",
            value: today,
            type: "date",
            operation: "lessThanOrEquals",
            variableOperation: "LIKE_IGNORE_CASE",
          },
          {
            name: "qc_submitted_on",
            value: dateBefore30Days,
            type: "date",
            operation: "greaterThanOrEquals",
            variableOperation: "LIKE_IGNORE_CASE",
          },
          {
            name: "entityStage",
            value: `${
              option?.id === "Rejected" || option === "Rejected"
                ? "%QCREJ%"
                : "%QCAP%"
            }`,
            type: "string",
            operation: "likeIgnoreCase",
            variableOperation: "LIKE_IGNORE_CASE",
          },
        ];
        multiQueryArray = [];
        break;
      default:
        break;
    }
    return { stageValue, finished, rawQueryArray, multiQueryArray };
  };

  const prepareTaskListRequest = (option) => {
    let stageValue = "";
    let finished = false;
    let rawQueryArray = [];
    let multiQueryArray = [];

    let taskListRequestData = getTaskListRequestData(
      stageValue,
      finished,
      rawQueryArray,
      multiQueryArray,
      option
    );
    const mainTaskListRequest = {
      processDefinitionKey: selectedOption.process_def_key,
      start: taskStart,
      size: 10,
      sort: "startTime",
      order: "desc",
      finished: taskListRequestData.finished,
      deleted: false,
      includeProcessVariables: true,
      stage: taskListRequestData.stageValue,
      raw_query: taskListRequestData.rawQueryArray,
      multi_select_query: taskListRequestData.multiQueryArray,
    };
    return mainTaskListRequest;
  };

  const modifyProcessInstanceData = (filterOption, isCancelled) => {
    setProcessInstanceData((prev) => {
      const updated = { ...prev };
      filterOption.forEach((op) => {
        if (updated[op.id]) {
          updated[op.id] = { ...updated[op.id], instanceData: op.processInstanceData };
        }
      });
      return updated;
    });

    const checkTotalData = filterOption.filter(
      (option) => option.id === selectedFilterOption
    )[0]?.processInstanceData;
    setSelectedUfo(checkTotalData);

    if (
      checkTotalData &&
      Object.keys(checkTotalData).length &&
      checkTotalData?.data.length < checkTotalData?.total
    ) {
      setHasMoreTasks(true);
    } else {
      setHasMoreTasks(false);
    }

    /** Set the updated filter options */
    setFilterOptions(filterOption);
    if (!isCancelled) {
      setLoading(false);
    } else {
      setLoading(true);
    }
  };

  const fetchScrolledFilterOptions = async (pagination, type) => {
    setLoadingTasks(true);

    const taskListRequest = prepareTaskListRequest(selectedFilterOption);

    /** to fetch specific project data */
    let pid = await getScrolledProcessInstances(taskListRequest);

    let resultObj = {
      id: selectedFilterOption,
      processInstanceData: pid[selectedFilterOption]?.instanceData,
      text: pid[selectedFilterOption]?.text,
    };

    let updatedFilterOptions = filterOptions.map((op) =>
      op.id === resultObj.id ? resultObj : op
    );

    setFilterOptions(updatedFilterOptions);
    setLoadingTasks(false);
  };

  const fetchFilterOptions = async (pagination, type) => {
    setLoading(true);
    let isCancelled = false;

    // Cancel all previous ongoing requests before making new ones
    if (abortControllerRef.current.processInstance.length > 0) {
      abortControllerRef.current.processInstance.forEach((controller) =>
        controller.abort()
      );

      // Clear the ref array after canceling all requests
      abortControllerRef.current.processInstance = [];
    }

    // Make new requests and track them
    const updatedFilterOptions = await Promise.all(
      dynamicFilterOptions.map(async (option) => {
        const taskListRequest = prepareTaskListRequest(option);

        /** to fetch specific project data */
        let pid = await getProcessInstances(taskListRequest, option);
        isCancelled = pid[1];

        /** Return the updated option with processInstanceData added */
        return {
          ...option,
          processInstanceData: Array.isArray(pid[0]) ? null : pid[0]?.[option.id]?.instanceData,
        };
      })
    );

    modifyProcessInstanceData(updatedFilterOptions, isCancelled);
  };

  const getOnboardTokenByUserId = async (userId) => {
    try {
      const response = await axios.post(
        `${urlLinks.baseUrl}/${urlLinks.taskModal}/${urlLinks.taskExecution.getOnboardTokenByUserId}?userId=${userId}`
      );
      setOnboardToken(response.data.response);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchProjectListDetails = async (userId) => {
    try {
      const response = await axios.get(
        `${urlLinks.baseUrl}/${urlLinks.taskModal}/${urlLinks.getProjects}?userId=${userId}`
      );

      const attendanceProjects = response.data.response;

      attendanceProjects.map((i) => {
        i.projectId = i.projects_id;
        i.projectName = i.project_name;
        delete i.projects_id;
        delete i.project_name;
        return true;
      });

      setProjectList(attendanceProjects);
    } catch (error) {
      console.log(error);
    }
  };

  /** useEffects */

  useEffect(() => {
    // Initial load and on page change
    if (hasMoreTasks) {
      fetchScrolledFilterOptions(true, "scrolling");
    }
  }, [taskStart]);

  useEffect(() => {
    if (loadingTasks) return;
    const debounceHandleScroll = debounce(handleScroll, 200);
    window.addEventListener("scroll", debounceHandleScroll);
    return () => {
      window.removeEventListener("scroll", debounceHandleScroll);
    };
  }, [loadingTasks, hasMoreTasks]);

  useEffect(() => {
    if (!dataFetched) return;
    setSelectedFilterOption(dynamicFilterOptions[0].id);
    setTaskStart(0);
    setProcessInstanceData(dynamicFilterOptionsObj);
    if (selectedOption.projectId === -1) return;

    fetchFilterOptions(false);
  }, [dataFetched, selectedOption, selectedTab]);

  useEffect(() => {
    let data = filterOptions.filter(
      (option) => option.id === selectedFilterOption
    )[0]?.processInstanceData;

    setProcessData(data);
  }, [selectedFilterOption, filterOptions]);

  useEffect(() => {
    if (!processData || !processData.data || processData.data.length === 0) {
      return setTableData([]);
    }
    let formFieldsObject = {};
    let dataArray = processData?.data;
    let otherFormFieldsArray = [
      "final_value",
      "ratecard_value",
      "logo",
      "caseNumber",
      "wf_assigned_on",
      "wf_submitted_on",
      "qc_submitted_on",
      "stage_due_date",
      "is_draft",
      "is_tat_expired",
      "entityStage",
      "admin_qc",
      "qc",
      "reject_reason",
    ];
    let formFieldsArray = processData?.selected_form_fields?.map(
      (field) => Object.keys(field)[0]
    );

    formFieldsArray = formFieldsArray
      ? [...formFieldsArray, ...otherFormFieldsArray]
      : [...otherFormFieldsArray];

    processData?.selected_form_fields?.map(
      (field) => (formFieldsObject = { ...formFieldsObject, ...field })
    );

    dataArray = dataArray.map((ele) => {
      return {
        ...ele,
        variablesModified: ele.variables.filter((e) =>
          formFieldsArray.includes(e.name)
        ),
      };
    });

    dataArray = dataArray.map((ele) => {
      return {
        ...ele,
        variablesModified: ele.variablesModified.map((e) => {
          return { ...e, rowName: formFieldsObject[e.name] };
        }),
      };
    });
    processData.data.requiredData = dataArray;

    setTableData(dataArray);
  }, [processData]);

  useEffect(() => {
    if (projectList.length === 0) return;
    if (selectedOption.projectId === -1 && projectList.length !== 0) {
      setSelectedOption(projectList[0]);
    }
  }, [projectList]);

  useEffect(() => {
    const currentPath = window.location.pathname.replace(/\/$/, "");
    if (currentPath === "/task-page") {
      setIsNavbarVisible(true);
    }
  }, [window.location.pathname]);

  useMemo(() => {
    setLoading(true);
    let userId = localStorage.getItem("userId");
    /** to fetch onboarded token */
    getOnboardTokenByUserId(userId).then(() => {
      setDataFetched(true); // Mark data as fetched after completion
    });

    /** to fetch project listing */
    fetchProjectListDetails(userId);
    setLoading(false);
  }, []);

  const loaderBar = (
    <div className="my-[130px] mx-auto flex justify-center">
      <ScaleLoader height={70} width={10} color={"#1c75bc"} />
    </div>
  );

  const loaderBarTask = (
    <div className="my-[10px] mx-auto flex justify-center">
      <ScaleLoader height={30} width={10} color={"#1c75bc"} />
    </div>
  );

  const noDataLayout = (
    <div className="flex flex-col items-center my-[80px]">
      <img src={noData} alt="no data" />
      <span className="mt-[22px] text-bold text-[18px]">
        No tasks available
      </span>
    </div>
  );

  const totalTaskLayout = (
    <div className="bg-[#F3F3F3] w-full py-[5px] px-[20px] text-[14px] mt-[15px] flex flex-col">
      <span>
        {processData?.total}&nbsp;
        {processData?.total > 1 ? "Tasks" : "Task"}
      </span>
      {selectedFilterOption === "Rejected" && (
        <span className="text-[11px] text-[#D70303] flex">
          <img src={information} alt="information" className="mr-[3px]" />
          Rejected cases will not be paid for
        </span>
      )}
    </div>
  );

  return (
    <section>
      <div className="px-[16px] pt-[16px] pb-[0]">
        <SingleDropdown
          uniqueId="taskExecution"
          projectList={projectList}
          selectedOption={selectedOption}
          setSelectedOption={setSelectedOption}
          handleInitialData={handleInitialData}
        />
      </div>
      <div>
        <Tabs
          tabOptions={tabOptions}
          selectedTab={selectedTab}
          handleTab={handleTab}
        />
        {loading ? (
          loaderBar
        ) : (
          <div>
            <div className={`hide-scrollbar flex overflow-x-scroll`}>
              {filterOptions.map((option, i) => (
                <Chips
                  uniqueId="taskExecution"
                  key={i}
                  data={option}
                  firstChild={i === 0}
                  lastChild={i === dynamicFilterOptions.length - 1}
                  selectedFilterOption={selectedFilterOption}
                  setSelectedFilterOption={setSelectedFilterOption}
                  selectedTab={selectedTab}
                />
              ))}
            </div>
            <div className="flex flex-col items-center">
              {selectedTab.id === "QcDone" && totalTaskLayout}
              {tableData.length > 0
                ? tableData?.map((el, i) => (
                    <JobCard
                      key={i}
                      keyId={i}
                      data={el}
                      selectedTab={selectedTab}
                      selectedFilterOption={selectedFilterOption}
                      loadingTasks={loadingTasks}
                    />
                  ))
                : noDataLayout}
              {loadingTasks ? loaderBarTask : <div></div>}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default TaskPage;
